import type { ChangeEvent } from 'react';

type FieldProps = {
  label: string;
  name: string;
  as?: 'input' | 'textarea';
  type?: string;
  rows?: number;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: string;
  placeholder?: string;
  /** Forwarded to the input, e.g. `"username"`/`"current-password"` on the
   * admin login form so password managers can offer to fill/save. */
  autoComplete?: string;
};

const controlClassName =
  'block min-h-[44px] w-full rounded border border-line bg-surface-2 px-[14px] py-[13px] font-sans text-base text-ink placeholder:text-ink-soft/70 focus:border-transparent focus:outline-2 focus:outline-primary focus:outline-offset-1';

/** Labeled input/textarea, matching the mockup's `.field`. Controlled via `value`/`onChange`. */
export const Field = ({
  label,
  name,
  as = 'input',
  type = 'text',
  rows = 5,
  value,
  onChange,
  error,
  placeholder,
  autoComplete,
}: FieldProps) => {
  const errorId = error ? `${name}-error` : undefined;

  return (
    <div className="mb-4 block">
      <label htmlFor={name} className="text-ink-soft mb-1.5 block text-[12.5px] tracking-[.02em]">
        {label}
      </label>
      {as === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          rows={rows}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          className={controlClassName}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          className={controlClassName}
        />
      )}
      {error ? (
        <p id={errorId} className="text-error-fg mt-1.5 text-sm">
          {error}
        </p>
      ) : null}
    </div>
  );
};
